import React, { useCallback, useMemo } from 'react'
import { withRouter, useLocation, useHistory } from 'react-router'

import { ResetPasswordModal } from '../AuthModals/ResetPasswordModal/ResetPasswordModal'
import { ForgotPasswordModal } from '../AuthModals/ForgotPasswordModal/ForgotPasswordModal'
import { LoginModal } from '../AuthModals/LoginModal/LoginModal'
import { CreateAccountModal } from '../AuthModals/CreateAccountModal/CreateAccountModal'
import { VerifyEmail } from '../VerifyEmail/VerifyEmail'
import { FulfillmentSelectorModal } from '../FulfillmentSelectorModal/FulfillmentSelectorModal'
import { MFAVerificationModal } from '../AuthModals/MFAVerificationModal/MFAVerificationModal'
import { SearchModal } from '../Search/SearchModal/SearchModal'
import { MealSelectorModal } from '../MealSelector/MealSelectorModal/MealSelectorModal'
import { useFulfillmentAvailability } from '../../hooks/use-fulfillment-availability'
import { useAvailability } from '@local/do-secundo-availability-provider'
import { useVariant } from '../../hooks/use-variant'
import { XP_MEAL_SELECTOR } from '../ExperimentsProvider/experiment-definitions'
import { MenuPerformanceModal } from '@local/do-secundo-performance-audit'
import { paramsToCleanUp } from './utils'
import {
  CreatePasswordlessAccountModal,
  LoginPasswordlessModal,
  PasswordlessCodeVerificationModal,
  ConnectAccountModal
} from '@local/do-secundo-auth-modals'
import {
  CompleteAccountModal,
  useIsAccountLinkingEnabled,
  useIsPasswordlessAuthEnabled,
  useIsDeleteAccountEnabled,
  DeleteAccountModalExperience,
  EmailDetectedModal,
  UnifiedLoginCreateAccountModal,
  UnifiedSmsVerificationModal,
  FROM_LOCATION
} from '@local/do-secundo-passwordless-authentication'
import { useFlag, FF } from '@local/do-secundo-feature-flag'

const ModeRouter = withRouter(({ location, history }) => {
  const searchParams = new URLSearchParams(location.search)
  searchParams.delete('mode')
  paramsToCleanUp.forEach((param) => searchParams.delete(param))
  const closeLocation = {
    ...location,
    search: searchParams.toString()
  }
  const pushLocation = () => history.push(closeLocation)
  const replaceLocationClearState = () => {
    const clearState = {
      ...closeLocation,
      state: {}
    }
    history.replace(clearState)
  }
  const replaceLocation = () => history.replace(closeLocation)
  const mealSelectorVariant = !!useVariant(XP_MEAL_SELECTOR.experimentName)
  const fulfillmentAvailable = useFulfillmentAvailability()
  const { orderingAvailable } = useAvailability()
  const {
    PASSWORDLESS_ENABLED,
    UNIFIED_CREATE_ACCOUNT_ENABLED,
    UNIFIED_LOGIN_ENABLED
  } = useIsPasswordlessAuthEnabled()
  const accountLinkingEnabled = useIsAccountLinkingEnabled()
  const DELETE_ACCOUNT_ENABLED = useIsDeleteAccountEnabled()
  const EDIT_MODE_ENABLED = useFlag(FF.EDIT_MODE)

  const routes = {
    login: <LoginModal onClose={pushLocation} />,
    create: <CreateAccountModal onClose={pushLocation} />,
    verify: <VerifyEmail onClose={pushLocation} />,
    search: <SearchModal onClose={pushLocation} />,
    forgot: <ForgotPasswordModal onClose={pushLocation} />,
    reset: <ResetPasswordModal onClose={replaceLocation} />,
    mfa: <MFAVerificationModal onClose={pushLocation} />
  }

  if (EDIT_MODE_ENABLED) {
    routes.performance = <MenuPerformanceModal onClose={pushLocation} />
  }

  if (PASSWORDLESS_ENABLED) {
    // v1 passwordless
    routes.login = <LoginPasswordlessModal onClose={pushLocation} />
    routes.create = (
      <CreatePasswordlessAccountModal onClose={replaceLocationClearState} />
    )
    routes.sms = (
      <PasswordlessCodeVerificationModal onClose={replaceLocationClearState} />
    )
    routes.complete = <CompleteAccountModal onClose={pushLocation} />

    if (accountLinkingEnabled) {
      routes.connect = <ConnectAccountModal onClose={pushLocation} />
      routes.connectLogin = <LoginModal onClose={pushLocation} />
    }
    if (DELETE_ACCOUNT_ENABLED) {
      routes.delete = <DeleteAccountModalExperience onClose={pushLocation} />
    }

    // v1.5 passwordless
    if (UNIFIED_CREATE_ACCOUNT_ENABLED) {
      routes.create =
        location?.state?.from === FROM_LOCATION.legacyLogin ||
        location?.state?.loyaltyAccountCreationInput ? (
          <CreatePasswordlessAccountModal onClose={replaceLocationClearState} />
        ) : (
          <UnifiedLoginCreateAccountModal
            onClose={replaceLocationClearState}
            headerText='Enter your phone number'
          />
        )
      routes.complete = (
        <CompleteAccountModal onClose={replaceLocationClearState} />
      )
      routes.emailDetected = (
        <EmailDetectedModal onClose={replaceLocationClearState} />
      )
      routes.sms =
        location?.state?.from === FROM_LOCATION.login ||
        location?.state?.from === FROM_LOCATION.loginFaster ||
        location?.state?.from === FROM_LOCATION.loyaltyCreate ? (
          // since login uses v1 passwordless mutations, we need to use a different modal to
          // handle sms code verification
          <PasswordlessCodeVerificationModal
            onClose={replaceLocationClearState}
          />
        ) : (
          <UnifiedSmsVerificationModal onClose={replaceLocationClearState} />
        )
    }

    // v2.0 passwordless replaces CreatePasswordlessAccountModal
    if (UNIFIED_LOGIN_ENABLED) {
      routes.login = (
        <UnifiedLoginCreateAccountModal
          onClose={pushLocation}
          headerText='Enter your phone number or email'
        />
      )
      routes.sms = (
        <UnifiedSmsVerificationModal onClose={replaceLocationClearState} />
      )
    }
  }

  if (fulfillmentAvailable) {
    routes.fulfillment = <FulfillmentSelectorModal onClose={replaceLocation} />
  }
  if (mealSelectorVariant && orderingAvailable) {
    routes.shuffle = <MealSelectorModal onClose={pushLocation} />
  }
  const search = new URLSearchParams(location.search)
  const mode = search.get('mode')
  return routes[mode] || null
})

export const useHasMode = (value) => {
  const location = useLocation()
  return new URLSearchParams(location.search).get('mode') === value
}

/**
 *
 * @param {Object} useModeHistoryPushCallbackParams
 * @param {string} useModeHistoryPushCallbackParams.mode
 * @param {import('history').LocationDescriptorObject|string} useModeHistoryPushCallbackParams.to - A location object or pathname string (note: replaces search with 'mode' parameter)
 */
export const useModeHistoryPushCallback = ({
  mode,
  to = { pathname: '/' }
}) => {
  const history = useHistory()
  const location = useLocation()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )
  searchParams.set('mode', mode)
  paramsToCleanUp.forEach((param) => searchParams.delete(param))

  if (typeof to === 'string') {
    to = { pathname: to }
  }

  return useCallback(() => {
    history.push({
      ...to,
      search: '?' + searchParams.toString()
    })
  }, [history, searchParams, to])
}

export { ModeRouter }
